/**
 * My WSS Service Layer
 * Centralizes all API calls for the My WSS feature
 * All API endpoints are inlined and self-contained
 */

import axios from "axios";
import { BASE_URL } from "@config/variables";
import { getUniqueListBy } from "@components/Common/Function";

/**
 * Service class for My WSS API operations
 */
class MyWssService {
  /**
   * Get WSS list by territory code
   * @param {string} territoryCode - SH2 Territory Code
   * @returns {Promise} API response with WSS list
   */
  async getWssList(territoryCode) {
    try {
      const query = `SELECT MIN(Id) Id, CustomerName__c, Customer_Code__c, TSI_Territory_Code__c, MIN(WSS_latitude__c) WSS_latitude__c, MIN(Updated_WSS_latitude__c) Updated_WSS_latitude__c, MIN(WSS_longitude__c) WSS_longitude__c, MIN(Updated_WSS_longitude__c) Updated_WSS_longitude__c, MIN(Monthly_Tour_Plan_CheckIn__c) Monthly_Tour_Plan_CheckIn__c, Town__c, TownCode__c FROM Account WHERE recordtype.Name IN ('Customer', 'CAPScouted') AND TSI_Territory_Code__c = '${territoryCode}' AND DiscontinueFlag__c = false GROUP BY Customer_Code__c, CustomerName__c, Town__c, TownCode__c, TSI_Territory_Code__c`;

      const response = await axios({
        url: `${BASE_URL}/services/data/v56.0/query?q=${query}`,
        method: "get",
      });

      if (response.status === 200) {
        return {
          success: true,
          data: response?.data || { records: [], totalSize: 0 },
          message: "WSS list fetched successfully",
        };
      }

      return {
        success: false,
        data: { records: [], totalSize: 0 },
        message: "Failed to fetch WSS list",
      };
    } catch (error) {
      console.error("Error fetching WSS list:", error);
      return {
        success: false,
        data: { records: [], totalSize: 0 },
        message: error.message || "Failed to fetch WSS list",
      };
    }
  }

  /**
   * Check in to a WSS
   * @param {Object} payload - Complete check-in payload
   * @param {string} payload.RecordtypeName - "Visit Plan WSS"
   * @param {string} payload.territory - Territory ID
   * @param {string} payload.customer - WSS name
   * @param {string} payload.customercode - WSS customer code
   * @param {number} payload.Transaction_lat - Current latitude
   * @param {number} payload.Transaction_long - Current longitude
   * @param {string} payload.IsValidCheckIn - "Valid" or "Invalid"
   * @param {number} payload.Master_lat - WSS master latitude
   * @param {number} payload.Master_long - WSS master longitude
   * @param {number} payload.Trasanction_distance - Distance in meters
   * @param {number} payload.Master_Accuracy - Master GPS accuracy
   * @param {number} payload.Current_Accuracy - Current GPS accuracy
   * @param {string} payload.Is_Correct_CheckIn - "Correct" or ""
   * @param {string} payload.Retail_counter_present - HO Auditor only
   * @param {string} payload.non_performing_status - HO Auditor only
   * @param {string} payload.Company_Name - HO Auditor only (if non-performing)
   * @returns {Promise} API response
   */
  async checkInWss(payload) {
    try {
      const response = await axios({
        url: `${BASE_URL}/services/apexrest/CheckIn_VisitPlan/`,
        method: "post",
        data: payload,
      });

      if (response.status === 200) {
        return {
          success: true,
          data: response?.data,
          message: "Check-in successful",
        };
      }

      return {
        success: false,
        data: null,
        message: "Check-in failed",
      };
    } catch (error) {
      console.error("Error checking in to WSS:", error);
      const apiError =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error.message ||
        "You are already checked-in for this WSS!";
      return {
        success: false,
        data: null,
        message: apiError,
      };
    }
  }

  /**
   * Get WSS check-in list for SH3 users (MyWssSummary)
   * Returns Visited, Not Visited, and New WSS lists
   * @param {Array} territoryList - Array of territory objects with SH2_Code__c
   * @param {boolean} divisionFlag - true for RBDM/STI users (division 70)
   * @returns {Promise} API response with categorized WSS lists
   */
  async getWssCheckInList(territoryList, divisionFlag = false) {
    try {
      if (!territoryList || territoryList.length === 0) {
        return {
          success: true,
          data: { Visited: [], NotVisited: [], New: [] },
          message: "No territories provided",
        };
      }

      // Extract unique territory codes
      const uniqueTerritories = getUniqueListBy(territoryList, "SH2_Code__c");
      const territoryCodes = uniqueTerritories.map((item) => item.SH2_Code__c);

      const response = await axios({
        url: `${BASE_URL}/services/apexrest/WSSDetailsList/`,
        method: "post",
        data: {
          territoryCodes: territoryCodes,
          RborstiUser: `${divisionFlag}`,
        },
      });

      if (response.status === 200) {
        return {
          success: true,
          data: response?.data || { Visited: [], NotVisited: [], New: [] },
          message: "WSS check-in list fetched successfully",
        };
      }

      return {
        success: false,
        data: { Visited: [], NotVisited: [], New: [] },
        message: "Failed to fetch WSS check-in list",
      };
    } catch (error) {
      console.error("Error fetching WSS check-in list:", error);
      return {
        success: false,
        data: { Visited: [], NotVisited: [], New: [] },
        message: error.message || "Failed to fetch WSS check-in list",
      };
    }
  }

  /**
   * Get territories for ZSM users
   * @param {string} sh3Code - SH3 Code for territory query
   * @returns {Promise} API response with territory list
   */
  async getZSMTerritories(sh3Code) {
    try {
      const query = encodeURIComponent(
        `Select id, SH2_Code__c, SH2_Name__c, SH3_Code__c, TXNType__c, SH6_Code__c, SH3_Name__c from Pidilite_Hierarchy__c where SH3_Code__c='${sh3Code}' AND RecordType.Name In('Sales Hierarchy','Key Information')`
      );

      const response = await axios({
        url: `${BASE_URL}/services/data/v56.0/query?q=${query}`,
        method: "get",
      });

      if (response.status === 200) {
        return {
          success: true,
          data: response?.data?.records || [],
          message: "Territories fetched successfully",
        };
      }

      return {
        success: false,
        data: [],
        message: "Failed to fetch territories",
      };
    } catch (error) {
      console.error("Error fetching ZSM territories:", error);
      return {
        success: false,
        data: [],
        message: error.message || "Failed to fetch territories",
      };
    }
  }

  /**
   * Get quarterly form flag for WSS (currently unused but kept for future)
   * @param {string} customerCode - WSS customer code
   * @returns {Promise} API response with quarterly flag
   */
  async getQuarterlyFormFlag(customerCode) {
    try {
      // This endpoint would be called if quarterly form logic is activated
      // Currently commented out in original MyWss.js but kept for completeness
      const response = await axios({
        url: `${BASE_URL}/services/apexrest/QuarterlyFormFlag/`,
        method: "post",
        data: { customerCode },
      });

      if (response.status === 200) {
        return {
          success: true,
          data: response?.data || { checked: false },
          message: "Quarterly form flag fetched successfully",
        };
      }

      return {
        success: false,
        data: { checked: false },
        message: "Failed to fetch quarterly form flag",
      };
    } catch (error) {
      console.error("Error fetching quarterly form flag:", error);
      return {
        success: false,
        data: { checked: false },
        message: error.message || "Failed to fetch quarterly form flag",
      };
    }
  }

  /**
   * Update an existing WSS check-in record (2-step audit flow)
   * Used by HO Auditor / SH5 / SH3 after initial check-in to attach audit data
   * @param {Object} payload - { Record_ID, Retail_counter_present, non_performing_status, Company_Name, ... }
   * @returns {Promise} API response
   */
  async updateCheckIn(payload) {
    try {
      const response = await axios({
        url: `${BASE_URL}/services/apexrest/CheckIn_VisitPlanUpdate/`,
        method: "post",
        data: payload,
      });

      if (response.status === 200) {
        return {
          success: true,
          data: response?.data,
          message: "Check-in updated successfully",
        };
      }

      return {
        success: false,
        data: null,
        message: "Failed to update check-in",
      };
    } catch (error) {
      console.error("Error updating WSS check-in:", error);
      const apiError =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error.message ||
        "Failed to update check-in";
      return {
        success: false,
        data: null,
        message: apiError,
      };
    }
  }

  /**
   * Get WSS check-in history to determine audit eligibility
   * Queries the last valid check-in for the given WSS code
   * @param {string} wssCode - WSS Code
   * @returns {Promise} API response with last check-in record
   */
  async getCheckInHistory(wssCode) {
    try {
      const query = encodeURIComponent(
        `SELECT Check_In_Date_Time__c,Visit_Status_es__c,WSS_Code__c,IsValidCheckIn__c FROM Monthly_Tour_Plan_es__c WHERE WSS_Code__c='${wssCode}' AND IsValidCheckIn__c='Valid' ORDER BY Check_In_Date_Time__c DESC LIMIT 1`
      );

      const response = await axios({
        url: `${BASE_URL}/services/data/v56.0/query?q=${query}`,
        method: "get",
      });

      if (response.status === 200) {
        return {
          success: true,
          data: response?.data?.records || [],
          message: "Check-in history fetched successfully",
        };
      }

      return {
        success: false,
        data: [],
        message: "Failed to fetch check-in history",
      };
    } catch (error) {
      console.error("Error fetching WSS check-in history:", error);
      return {
        success: false,
        data: [],
        message: error.message || "Failed to fetch check-in history",
      };
    }
  }
}

// Export singleton instance
export const myWssService = new MyWssService();
export default myWssService;
